import React, { useEffect, useRef, useState } from "react";
import PropTypes from "prop-types";
import { TabView, TabPanel } from "primereact/tabview";
import { Button } from "primereact/button";
import { DataTable } from "primereact/datatable";
import { Column } from "primereact/column";
import { Tag } from "primereact/tag";
import { InputText } from "primereact/inputtext";
import { Image } from "primereact/image";
import { Toast } from "primereact/toast";
import { Dialog } from "primereact/dialog";
import { IconField } from "primereact/iconfield";
import { InputIcon } from "primereact/inputicon";
import { FilterMatchMode } from "primereact/api";
import { useTranslation } from "react-i18next";
import { FloatLabel } from "primereact/floatlabel";
import { Dropdown } from "primereact/dropdown";
import "./download.css";
import axios from "axios";

const DownloadDadosModal = ({ active, close }) => {
  const { t } = useTranslation();
  const toast = useRef(null);

  const [show, setShow] = useState(false);
  const [layers, setLayers] = useState([]);
  const [metadados, setMetadados] = useState([]);
  const [isInitialLoad, setIsInitialLoad] = useState(true);

  const [filtersLayers, setFiltersLayers] = useState({
    global: { value: null, matchMode: FilterMatchMode.CONTAINS }
  });
  const [globalFilterLayers, setGlobalFilterLayers] = useState("");

  const [globalFilterMeta, setGlobalFilterMeta] = useState("");
  const [arquivoConteudo, setArquivoConteudo] = useState("");
  const [showArquivoModal, setShowArquivoModal] = useState(false);

  // Sincroniza show com a prop active vinda do pai (abertura)
  useEffect(() => {
    setShow(active);
  }, [active]);

  // Fechamento: avisa o pai via prop "close" (nome que o pai realmente usa)
  const handleClose = () => {
    setShow(false);

    if (close) {
      close();
    }
  };

  // Workspaces do GeoServer que devem ser consultados e exibidos juntos
  const GEOSERVER_WORKSPACES = ['zcm', 'semace'];

  const fetchWorkspaceCapabilities = async workspace => {
    try {
      const response = await axios.get(
        `https://hgpedea.sema.ce.gov.br/geoserver/${workspace}/wms?service=WMS&version=1.3.0&request=GetCapabilities`,
        { headers: { 'Content-Type': 'application/xml' } }
      );
      const xml = response.data;
      const parser = new DOMParser();
      const xmlDoc = parser.parseFromString(xml, 'text/xml');
      const layerElements = xmlDoc.querySelectorAll('Layer > Layer');
      const parsedLayers = [];

      layerElements.forEach(layer => {
        const name = layer.querySelector('Name')?.textContent;
        const title = layer.querySelector('Title')?.textContent;

        let bbox = null;
        let crs = 'EPSG:4326';

        const bboxEl = layer.querySelector('EX_GeographicBoundingBox');
        if (bboxEl) {
          const west = bboxEl.querySelector('westBoundLongitude')?.textContent;
          const east = bboxEl.querySelector('eastBoundLongitude')?.textContent;
          const south = bboxEl.querySelector('southBoundLatitude')?.textContent;
          const north = bboxEl.querySelector('northBoundLatitude')?.textContent;
          if (west && east && south && north) {
            bbox = `${south},${west},${north},${east}`;
          }
        } else {
          const altBboxEl = layer.querySelector('BoundingBox');
          if (altBboxEl) {
            crs = altBboxEl.getAttribute('CRS') || 'EPSG:4326';
            const minx = altBboxEl.getAttribute('minx');
            const miny = altBboxEl.getAttribute('miny');
            const maxx = altBboxEl.getAttribute('maxx');
            const maxy = altBboxEl.getAttribute('maxy');
            if (minx && miny && maxx && maxy) {
              bbox = `${minx},${miny},${maxx},${maxy}`;
            }
          }
        }

        let preview = null;
        if (bbox && name) {
          preview = `https://hgpedea.sema.ce.gov.br/geoserver/${workspace}/wms?service=WMS&version=1.3.0&request=GetMap&layers=${workspace}:${name}&styles=&bbox=${bbox}&width=200&height=120&crs=${crs}&format=image/png&transparent=true`;
        }

        if (name && title) {
          parsedLayers.push({
            id: `${workspace}:${name}`,
            name,
            title,
            preview,
            workspace
          });
        }
      });

      return parsedLayers;
    } catch (error) {
      console.error(`Erro ao buscar GetCapabilities do workspace "${workspace}":`, error);
      return [];
    }
  };

  useEffect(() => {
    const fetchAllCapabilities = async () => {
      const results = await Promise.all(
        GEOSERVER_WORKSPACES.map(workspace => fetchWorkspaceCapabilities(workspace))
      );
      setLayers(results.flat());
    };

    if (isInitialLoad) {
      fetchAllCapabilities();
      setIsInitialLoad(false);
    }
  }, [isInitialLoad]);

  const downloadGeoserver = (workspace, name, format) => {
    const url = `https://hgpedea.sema.ce.gov.br/geoserver/${workspace}/ows?service=WFS&version=1.1.0&request=GetFeature&typeName=${workspace}:${name}&outputFormat=${format}&srsName=EPSG:4674`;
    window.open(url, "_blank");
  };

  const onGlobalFilterLayersChange = e => {
    const value = e.target.value;
    let _filters = { ...filtersLayers };
    _filters.global.value = value;
    setFiltersLayers(_filters);
    setGlobalFilterLayers(value);
  };

  const headerLayers = (
    <div className="DownloadHeaderWrapper">
      <FloatLabel className="DownloadFloatLabel">
        <IconField iconPosition="right" className="DownloadIconField">
          <InputIcon className="pi pi-search" />
          <InputText
            id="searchLayers"
            value={globalFilterLayers}
            onChange={onGlobalFilterLayersChange}
            className="DownloadInputSearch"
          />
        </IconField>
        <label htmlFor="searchLayers">Buscar camada</label>
      </FloatLabel>
    </div>
  );

  const headerMetadados = (
    <div className="DownloadHeaderWrapper">
      <FloatLabel className="DownloadFloatLabel">
        <IconField iconPosition="right" className="DownloadIconField">
          <InputIcon className="pi pi-search" />
          <InputText
            id="searchMetadados"
            value={globalFilterMeta}
            onChange={e => setGlobalFilterMeta(e.target.value)}
            className="DownloadInputSearch"
          />
        </IconField>
        <label htmlFor="searchMetadados">Buscar metadado</label>
      </FloatLabel>
    </div>
  );

  const imageBodyTemplate = rowData => {
    return (
      <div className="DownloadImageWrapper">
        <img
          src={rowData.preview}
          alt={`Preview de ${rowData.name}`}
          className="DownloadPreviewImg"
          loading="lazy"
        />
      </div>
    );
  };

  useEffect(() => {
    const fetchMetadados = async () => {
      try {
        const response = await fetch(
          "https://hgpedea.sema.ce.gov.br/gestor-api/allmetadados"
        );
        if (!response.ok) throw new Error();
        const data = await response.json();
        setMetadados(data);
      } catch {
        console.error()
      }
    };

    if (isInitialLoad) {
      fetchMetadados();
    }
  }, [isInitialLoad]);

  const openArquivo = async name => {
    try {
      const response = await fetch(
        `https://hgpedea.sema.ce.gov.br/gestor-api/arquivotxt/${name}`
      );
      if (!response.ok) throw new Error();
      const text = await response.text();
      setArquivoConteudo(text);
      setShowArquivoModal(true);
    } catch {
      toast.current?.show({
        severity: "warn",
        summary: "Aviso",
        detail: "Erro ao abrir arquivo",
        life: 4000
      });
    }
  };

  const downloadArquivo = async name => {
    try {
      const response = await fetch(
        `https://hgpedea.sema.ce.gov.br/gestor-api/downloadMetadado/${name}`
      );
      if (!response.ok) throw new Error();

      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      link.download = `${name}.txt`;
      link.click();
    } catch {
      toast.current?.show({
        severity: "error",
        summary: "Erro",
        detail: "Erro ao baixar arquivo",
        life: 4000
      });
    }
  };

  const downloadFormatOptions = [
    { label: 'Shapefile (ZIP)', value: 'shape-zip' },
    { label: 'KML', value: 'kml' }
  ];

  const dialogFooter = (
    <div className="DownloadModalFooter">
      <Button
        label={t("common.close") || "Fechar"}
        icon="pi pi-times"
        className="CloseBtn"
        onClick={handleClose}
      />
    </div>
  );

  return (
    <>
      <Toast ref={toast} />

      <Dialog
        visible={show}
        onHide={handleClose}
        header="Download de Dados"
        className="DownloadModal"
        modal
        draggable={false}
        resizable={false}
        dismissableMask={false}
        closable={true}
        footer={dialogFooter}
        style={{ width: "90vw", maxWidth: "1200px" }}
        contentStyle={{ overflow: "auto" }}
      >
        <div className="DownloadContainer">
          <p className="DownloadTitle">
            Escolha abaixo os dados do GeoServer PEDEA ou acesse as informações e metadados das camadas disponíveis na PEDEA.
          </p>

          <TabView className="DownloadTabView">
            <TabPanel header={t("downloadDados.tabs.geoserver") || "GeoServer"}>
              <DataTable
                value={layers}
                paginator
                rows={5}
                rowsPerPageOptions={[5, 10, 25, 50]}
                stripedRows
                showGridlines
                dataKey="id"
                filters={filtersLayers}
                globalFilterFields={["title", "name", "workspace"]}
                header={headerLayers}
                emptyMessage="Nenhuma camada encontrada."
                className="DownloadDataTable"
              >
                <Column 
                  header="Imagem" 
                  body={imageBodyTemplate} 
                  style={{ width: '90px', maxWidth: '90px' }}
                />
                <Column 
                  header="Título" 
                  body={row => <Tag value={row.title} className="DownloadTag" rounded />}
                  style={{ width: '26%' }}
                />
                <Column 
                  header="Nome" 
                  body={row => <Tag value={row.name} className="DownloadTag" rounded />}
                  style={{ width: '30%' }}
                />
            
                <Column 
                  header="Baixar" 
                  body={row => (
                    <Dropdown
                      value={null}
                      options={downloadFormatOptions}
                      onChange={(e) => downloadGeoserver(row.workspace, row.name, e.value)}
                      placeholder="Baixar"
                      className="DownloadDropdown"
                      panelClassName="DownloadDropdownPanel"
                      showClear={false}
                    />
                  )}
                  style={{ width: '150px', maxWidth: '150px' }}
                />
              </DataTable>
            </TabPanel>

            <TabPanel header={t("downloadDados.tabs.metadados") || "Metadados"}>
              <DataTable
                value={metadados.filter(m => m.id_camada)}
                globalFilter={globalFilterMeta}
                paginator
                rows={5}
                rowsPerPageOptions={[5, 10, 25, 50]}
                stripedRows
                showGridlines
                header={headerMetadados}
                className="DownloadDataTable"
                emptyMessage="Nenhum metadado encontrado."
              >
                <Column field="camada.categoria_de_informacao" header="Categoria" style={{ width: '30%' }} />
                <Column field="camada.nomenclatura_pedea" header="Nome (Titulo Geoserver)" style={{ width: '40%' }} />
                <Column
                  header="Visualizar"
                  body={row => (
                    <i
                      className="pi pi-file"
                      style={{ cursor: "pointer", fontSize: "1.1rem", color: "var(--wm-forest)" }}
                      onClick={() => openArquivo(row.name)}
                    />
                  )}
                  style={{ width: '80px', textAlign: 'center' }}
                />
                <Column
                  header="Baixar"
                  body={row => (
                    <i
                      className="pi pi-download"
                      style={{ cursor: "pointer", fontSize: "1.1rem", color: "var(--wm-forest)" }}
                      onClick={() => downloadArquivo(row.name)}
                    />
                  )}
                  style={{ width: '80px', textAlign: 'center' }}
                />
              </DataTable>
            </TabPanel>
          </TabView>
        </div>
      </Dialog>

      <Dialog
        header="Conteúdo do Arquivo"
        visible={showArquivoModal}
        onHide={() => setShowArquivoModal(false)}
        className="DownloadArquivoDialog"
        modal
        draggable={false}
        style={{ width: "60vw", maxWidth: "800px" }}
      >
        <pre className="DownloadArquivoContent">{arquivoConteudo}</pre>
      </Dialog>
    </>
  );
};

DownloadDadosModal.propTypes = {
  active: PropTypes.bool,
  close: PropTypes.func
};

export default DownloadDadosModal;